import { useReactFlow } from '@xyflow/react';
import { useMemo, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'src/components/button';
import FormInput from 'src/components/form-input';
import { useGlobalContext } from 'src/context/global/use-global-context';
import { useWizardContext } from 'src/context/wizard/use-wizard-context';
import { exportAutoConfiguration } from 'src/utils/export-auto-configuration';
import {
  getVariableValue,
  applyVariablesToVlans,
  applyVariablesToNodes,
  applyVariablesToString,
} from 'src/utils/wizard';

import {
  EDataType,
  EDataSwitchType,
  NodeCategoryModel,
} from '../../models/node-data';
import { globalSettingsDefault } from '../global-settings';
import WizardAccordionNode from './wizard-accordion-node';
import WizardGuide from './wizard-guide';
import WizardVariableOptions from './wizard-variable-options';
import WizardVlanTable from './wizard-vlan-table';

interface WizardExportAutoConfigurationProps {
  selectedNode: string;
  targets: {
    id: string;
    name: string;
  }[];
  errorAutoConfiguration: string;
}

export default function WizardExportAutoConfiguration({
  selectedNode,
  targets,
  errorAutoConfiguration,
}: WizardExportAutoConfigurationProps) {
  const {
    useCustomVlan,
    vlans: originalVlans,
    projectData,
    globalSettings,
  } = useGlobalContext();
  const { getNodes, getEdges } = useReactFlow();
  const {
    currentVlans,
    currentNodes,
    currentVariableOptions,
    numberOfFilesToExport,
    setNumberOfFilesToExport,
    syncWithOriginalData,
    handleUpdateInterface,
    handleUpdateNode,
    handleUpdateVlan,
    handleValidateAllVariableOptions,
    hasVariableErrors,
    variableInfo,
    currentSwitches,
    currentDevices,
  } = useWizardContext();

  const nodes = useMemo(() => getNodes(), [getNodes]);

  // Sync with original data when modal opens or data changes
  useEffect(() => {
    syncWithOriginalData(originalVlans, nodes);
  }, [nodes, syncWithOriginalData, originalVlans]);

  const exportZipBundle = async () => {
    if (!selectedNode) {
      toast.error('No switches selected');
      return;
    }

    if (hasVariableErrors) {
      toast.error('Please fix all variable errors before exporting');
      return;
    }

    try {
      const edges = getEdges();
      const allNodes = getNodes();
      const allNodeSwitches = allNodes.filter(
        (node) =>
          (node.data?.category as NodeCategoryModel)?.type ===
            EDataType.Switch &&
          (node.data?.category as NodeCategoryModel)?.switchType?.includes(
            EDataSwitchType.MANAGED,
          ),
      );

      if (allNodeSwitches.length === 0) {
        toast.error('No switches found');
        return;
      }

      for (
        let fileIndex = 0;
        fileIndex < numberOfFilesToExport;
        fileIndex += 1
      ) {
        const resolvedVlans = applyVariablesToVlans(
          currentVariableOptions,
          currentVlans,
          fileIndex,
        );
        const resolvedNodes = applyVariablesToNodes(
          currentVariableOptions,
          currentNodes,
          fileIndex,
        );
        const resolvedProjectName = applyVariablesToString(
          currentVariableOptions,
          projectData?.projectName || 'deployment',
          fileIndex,
        );
        // add suffix first variable to project name
        const firstVariableValue =
          variableInfo.items.length > 0
            ? getVariableValue(
                currentVariableOptions,
                variableInfo.items[0],
                fileIndex,
              )
            : '';
        const resolvedProjectNameSuffix = firstVariableValue
          ? `${resolvedProjectName}-${firstVariableValue}`
          : resolvedProjectName;
        exportAutoConfiguration({
          nodes: resolvedNodes,
          edges,
          vlans: resolvedVlans,
          targets,
          selectedNode,
          projectName: resolvedProjectNameSuffix,
          globalSettings: globalSettings ?? globalSettingsDefault,
        });
      }
      toast.success('Zip bundle exported successfully');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('exportZipBundle ~ error:', error);
      toast.error('Failed to export zip bundle');
    }
  };

  return (
    <div className="flex w-full flex-col items-start justify-center gap-1 border-t border-gray-200 pt-2">
      <WizardGuide />
      <div className="flex w-full flex-row items-center justify-center gap-2">
        <label
          htmlFor="number-of-files-to-export"
          className="whitespace-nowrap text-sm font-medium text-gray-900"
        >
          Number of files to export
        </label>
        <FormInput
          id="number-of-files-to-export"
          name="number-of-files-to-export"
          type="number"
          data-testid="number-of-files-to-export"
          min={1}
          className="w-auto"
          value={numberOfFilesToExport}
          onChange={(e) => {
            const newValue = Number(e.target.value);
            setNumberOfFilesToExport(newValue);
            // Pass newValue — closure would still see the previous file count
            handleValidateAllVariableOptions(newValue);
          }}
        />
      </div>
      <div className="mt-2 flex w-full flex-row items-start justify-center gap-4">
        <div className="flex w-1/2 flex-col items-start justify-center gap-1">
          {useCustomVlan && (
            <WizardVlanTable
              vlans={currentVlans}
              vlanVariables={variableInfo.vlanVariables}
              onUpdateVlan={handleUpdateVlan}
            />
          )}
          <WizardAccordionNode
            title="Switches"
            allVariables={variableInfo.switchVariables}
            nodes={currentSwitches}
            useCustomVlan={useCustomVlan}
            handleUpdateInterface={handleUpdateInterface}
            handleUpdateNode={handleUpdateNode}
          />
          <WizardAccordionNode
            title="Devices"
            allVariables={variableInfo.deviceVariables}
            nodes={currentDevices}
            useCustomVlan={useCustomVlan}
            handleUpdateInterface={handleUpdateInterface}
            handleUpdateNode={handleUpdateNode}
          />
        </div>
        <div className="flex w-1/2 flex-col items-start justify-center gap-1">
          <WizardVariableOptions />
        </div>
      </div>
      <div className="mt-4 flex w-full items-center justify-end">
        <div className="flex flex-col items-end justify-center gap-1">
          {errorAutoConfiguration && (
            <span className="text-red-500">{errorAutoConfiguration}</span>
          )}
          {!selectedNode && (
            <span className="text-red-500">No switches selected</span>
          )}
          {hasVariableErrors && (
            <span className="text-red-500">
              Please fix all variable errors before exporting
            </span>
          )}
          <Button
            type="button"
            data-testid="dialog-package-export-zip-bundle-button"
            onClick={exportZipBundle}
            disabled={
              !!errorAutoConfiguration || !selectedNode || hasVariableErrors
            }
          >
            Export zip bundle
          </Button>
        </div>
      </div>
    </div>
  );
}
